import {
  type Dir,
  type IceServerMessage,
  type IceSnapshot,
  levelById,
  type Pos,
  type Reaction,
  step,
} from '@xox/shared'
import { create } from 'zustand'
import { FALL_MS } from '../ice/anim'
import { type Effect, feedback } from '../lib/feedback'
import type { ConnectionStatus, RoomSocket } from '../lib/socket'
import {
  type CommonView,
  commonActions,
  initialCommonView,
  openRoomSocket,
  reduceCommon,
} from './shared'

export interface IceView extends CommonView {
  snapshot: IceSnapshot | null
  /** racer id → hole position while their fall animates (state already has them at the start). */
  falling: Record<string, Pos>
}

interface IceStore extends IceView {
  code: string | null
  status: ConnectionStatus
  connect: (code: string) => void
  disconnect: () => void
  takeSeat: () => void
  start: (levelId: string) => void
  move: (dir: Dir) => void
  next: () => void
  leave: () => void
  react: (emoji: Reaction) => void
  expireReaction: (id: string) => void
  clearFalling: (id: string) => void
  dismissError: () => void
}

export function reduceIceMessage(
  view: IceView,
  msg: IceServerMessage,
  now = Date.now(),
): Partial<IceView> {
  const common = reduceCommon(view, msg, now)
  if (common) return common
  switch (msg.type) {
    case 'state': {
      if (view.snapshot && msg.snapshot.seq < view.snapshot.seq) return {}
      return { snapshot: msg.snapshot, closedReason: null }
    }
    case 'moved': {
      if (msg.outcome !== 'fell' && msg.outcome !== 'caught') return {}
      const hole = msg.path[msg.path.length - 1]
      return hole ? { falling: { ...view.falling, [msg.playerId]: hole } } : {}
    }
    default:
      return {}
  }
}

/** Own slides make noise; rivals only chime when they finish, so 8 racers don't become a racket. */
export function deriveIceEffects(prev: IceView, next: IceView, msg: IceServerMessage): Effect[] {
  if (msg.type === 'reaction') return ['react']
  if (msg.type === 'moved') {
    const mine = msg.playerId === next.playerId
    if (msg.outcome === 'door') return [mine ? 'door' : 'tick']
    if (!mine) return []
    const out: Effect[] = [
      msg.outcome === 'fell' ? 'splash' : msg.outcome === 'caught' ? 'caught' : 'slide',
    ]
    if (msg.gotKey) out.push('key')
    return out
  }
  if (msg.type !== 'state') return []
  const a = prev.snapshot
  const b = next.snapshot
  if (!a || !b || b.seq <= a.seq || a.phase === b.phase) return []
  if (b.phase === 'countdown') return ['countdown']
  if (b.phase === 'racing') return ['go']
  if (b.phase === 'results') {
    const me = next.playerId ? b.racers.find((r) => r.profile.id === next.playerId) : undefined
    const cue: Effect = !me || me.place === 1 ? 'win' : 'lose'
    // The crowd only cheers when somebody actually reached the door.
    return b.racers.some((r) => r.place !== null) ? ['cheer', cue] : [cue]
  }
  return []
}

let socket: RoomSocket | null = null

/** Server clock as this room's socket sees it. */
export function iceServerNow(): number {
  return Date.now() + useIce.getState().clockOffset
}

export const useIce = create<IceStore>((set, get) => ({
  ...initialCommonView,
  code: null,
  status: 'closed',
  snapshot: null,
  falling: {},

  connect(code) {
    if (socket && get().code === code) return
    socket?.close()
    set({ ...initialCommonView, code, snapshot: null, falling: {}, status: 'connecting' })
    socket = openRoomSocket<IceView, IceServerMessage>(code, '/ws/ice', {
      get,
      set,
      setStatus: (status) => set({ status }),
      reduce: reduceIceMessage,
      effects: deriveIceEffects,
      expireReaction: (id) => get().expireReaction(id),
      after: (msg) => {
        if (msg.type === 'moved' && (msg.outcome === 'fell' || msg.outcome === 'caught')) {
          setTimeout(() => get().clearFalling(msg.playerId), FALL_MS)
        }
      },
    })
  },

  disconnect() {
    socket?.close()
    socket = null
    set({ code: null, snapshot: null, status: 'closed', reactions: [], falling: {} })
  },

  ...commonActions<IceStore>(() => socket, set),
  start(levelId) {
    socket?.send({ type: 'start', levelId })
  },
  move(dir) {
    const { snapshot, playerId } = get()
    const me = snapshot?.racers.find((r) => r.profile.id === playerId)
    if (snapshot?.phase !== 'racing' || !me || me.finishedAt !== null) return
    // Blocked moves never reach the server (no state change); give local feedback instead.
    const level = snapshot.levelId ? levelById(snapshot.levelId) : undefined
    if (level) {
      const r = step(
        level,
        { pos: me.pos, moves: me.moves, cracked: me.cracked, hasKey: me.hasKey },
        dir,
      )
      if (r.path.length === 0) {
        feedback('thud')
        return
      }
    }
    socket?.send({ type: 'move', dir })
  },
  next() {
    socket?.send({ type: 'next' })
  },
  clearFalling(id) {
    set((s) => {
      const { [id]: _gone, ...falling } = s.falling
      return { falling }
    })
  },
}))
